import { Availability, AvailabilityType } from './availablityBackendTypes';

export enum SourcingAvailabilityStatus {
    InStock = 'InStock',
    LeadTime = 'LeadTime',
    OnOrder = 'OnOrder',
    OnOrderUnknownLeadTime = 'OnOrderUnknownLeadTime',
    NotSpecified = 'NotSpecified',
}

export interface SourcingAvailability {
    status: SourcingAvailabilityStatus;
    lead_time_in_days: number | null;
    shipping_time_in_days: number | null;
    delivery_time_in_days: number | null;
    shipping_time_info?: {
        label: string;
        source: 'SupplierShipping' | 'OfferDelivery';
    };
}

type SourcingAvailabilityOptions = {
    shipping_time_label?: string;
    offer_delivery?: {
        days: number;
        label: string;
    };
};

function computeDeliveryTime(leadTimeDays: number | null, shippingDays: number | null): number | null {
    if (leadTimeDays === null && shippingDays === null) {
        return null;
    }
    return (leadTimeDays ?? 0) + (shippingDays ?? 0);
}

function createMetadata(options: SourcingAvailabilityOptions): {
    shipping_time_info: SourcingAvailability['shipping_time_info'];
} {
    if (options.offer_delivery) {
        return {
            shipping_time_info: { label: options.offer_delivery.label, source: 'OfferDelivery' },
        };
    }

    if (options.shipping_time_label) {
        return {
            shipping_time_info: { label: options.shipping_time_label, source: 'SupplierShipping' },
        };
    }

    return { shipping_time_info: undefined };
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace SourcingAvailability {
    /** As the backend's `AvailabilityWithShippingTime::aggregate()`; an unknown lead time gets no delivery time. */
    export function create(
        availability: Availability | null | undefined,
        options: SourcingAvailabilityOptions = {},
    ): SourcingAvailability {
        const metadata = createMetadata(options);

        if (!availability) {
            return {
                status: SourcingAvailabilityStatus.NotSpecified,
                lead_time_in_days: null,
                shipping_time_in_days: null,
                delivery_time_in_days: null,
                shipping_time_info: metadata.shipping_time_info,
            };
        }

        const shippingDays = options.offer_delivery?.days ?? availability.shipping_time_in_days ?? null;

        switch (availability.type) {
            case AvailabilityType.Stock:
                return {
                    status: SourcingAvailabilityStatus.InStock,
                    lead_time_in_days: null,
                    shipping_time_in_days: shippingDays,
                    delivery_time_in_days: shippingDays,
                    shipping_time_info: metadata.shipping_time_info,
                };

            case AvailabilityType.LeadTime:
                return {
                    status: SourcingAvailabilityStatus.LeadTime,
                    lead_time_in_days: availability.days,
                    shipping_time_in_days: shippingDays,
                    delivery_time_in_days: computeDeliveryTime(availability.days, shippingDays),
                    shipping_time_info: metadata.shipping_time_info,
                };

            case AvailabilityType.OnOrder:
                return {
                    status: SourcingAvailabilityStatus.OnOrder,
                    lead_time_in_days: availability.days,
                    shipping_time_in_days: shippingDays,
                    delivery_time_in_days: computeDeliveryTime(availability.days, shippingDays),
                    shipping_time_info: metadata.shipping_time_info,
                };

            case AvailabilityType.OnOrderWithUnknownLeadTime:
                return {
                    status: SourcingAvailabilityStatus.OnOrderUnknownLeadTime,
                    lead_time_in_days: null,
                    shipping_time_in_days: shippingDays,
                    delivery_time_in_days: null,
                    shipping_time_info: metadata.shipping_time_info,
                };

            default: {
                const _exhaustive: never = availability;
                throw new Error(`Unhandled availability type: ${JSON.stringify(_exhaustive)}`);
            }
        }
    }
}
